import { inject } from '@angular/core';
import { canGuard } from '@kinet-open-network/client/auth/data-access';
import { formBuilder, select } from '@kinet-open-network/client/core/forms/ui';
import { provideDocuments } from '@kinet-open-network/client/documents/data-access';
import {
  BaseFlowsService,
  BaseFlowsStore,
  FlowTypeConfig,
  provideFlowTypeConfig,
} from '@kinet-open-network/client/flows/data-access';
import { createEnumList } from '@kinet-open-network/shared/data-access/model';
import {
  FlowGoodsReceiptActions,
  flowGoodsReceiptActionsOptions,
  FlowType,
  goodsReceiptStatusOptions,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { of } from 'rxjs';
import { AppCustomer } from '../../../models/app-customer.model';
import { FlowGoodsReceiptDetailComponent } from '../feature/flow-goods-receipt.detail.component';
import { FlowGoodsReceipts } from './flow-goods-receipts.model';
import { FlowGoodsReceiptsService } from './flow-goods-receipts.service';
import { FlowGoodsReceiptsStore } from './flow-goods-receipts.store';

export const goodsReceiptFlowConfig: FlowTypeConfig<FlowGoodsReceipts> = {
  type: FlowType.goodsReceipt,
  label: 'Príjem tovaru',
  icon: 'pi-cart-arrow-down',
  subject: 'GoodsReceipt',
  visible: (ability) => ability.can('read', 'GoodsReceipt'),
  createComponent: FlowGoodsReceiptDetailComponent,
  detailComponent: FlowGoodsReceiptDetailComponent,
  providers: [
    FlowGoodsReceiptsStore,
    { provide: BaseFlowsStore, useExisting: FlowGoodsReceiptsStore },
    { provide: BaseFlowsService, useExisting: FlowGoodsReceiptsService },
    FlowGoodsReceiptsService,
  ],
  store: FlowGoodsReceiptsStore,
  service: FlowGoodsReceiptsService,
  labels: {
    createLabel: 'Nový príjem tovaru',
    detailLabel: 'Príjem tovaru',
    listLabel: 'Príjem tovaru',
  },
  createFromCustomerVisible: (customer: AppCustomer) => !!customer.isSupplier,
  auditType: 'goods-receipts',
  actions: {
    actionOptions: flowGoodsReceiptActionsOptions,
    actionFields: new Map([
      [
        FlowGoodsReceiptActions.changeStatus,
        () =>
          formBuilder([
            select('status', 'Zmeniť stav')
              .options(createEnumList(goodsReceiptStatusOptions))
              .required(),
          ]),
      ],
    ]),
  },
  columns: [
    { key: 'id', label: 'Číslo', filter: 'text' },
    { key: 'customer.fullname', label: 'Dodávateľ', filter: 'text' },
    {
      key: 'status',
      label: 'Stav',
      filter: {
        type: 'select',
        options: goodsReceiptStatusOptions,
        showColor: true,
        showIcon: true,
      },
    },
    { key: 'stock.name', label: 'Sklad', filter: 'text' },
    { key: 'date', label: 'Dátum', filter: 'date' },
    { key: 'extId', label: 'Externé číslo', filter: 'text' },
    { key: 'total', label: 'Spolu s DPH', filter: 'currency' },
    { key: 'totalBase', label: 'Spolu bez DPH', filter: 'currency' },
    { key: 'totalVat', label: 'DPH spolu', filter: 'currency' },
    { key: 'note', label: 'Poznámka', filter: 'text', hidden: true },
  ],
  detailChildrenRoutes: [
    {
      path: '',
      loadComponent: () =>
        import('../feature/tabs/flow-goods-receipt-general-tab.component').then(
          (m) => m.FlowGoodsReceiptGeneralTabComponent,
        ),
      // Nested so the general tab stays mounted behind the scan overlay.
      children: [
        {
          path: 'selection',
          canActivate: [canGuard('update', 'GoodsReceipt')],
          loadComponent: () =>
            import('../feature/flow-goods-receipt-selection.component').then(
              (m) => m.FlowGoodsReceiptSelectionComponent,
            ),
        },
      ],
    },
    {
      path: 'documents',
      title: 'Dokumenty',
      providers: [
        provideDocuments(() => {
          const store = inject(FlowGoodsReceiptsStore);
          return {
            parentType: 'goods-receipts',
            templateContextType: 'goods-receipt',
            getSendEmailOptions: () => {
              const options = (store.model()?.customer?.contacts ?? [])
                .filter((c): c is typeof c & { email: string } => !!c.email)
                .map((c) => ({
                  label: c.contactName
                    ? `${c.contactName} <${c.email}>`
                    : c.email,
                  value: c.email,
                }));

              return of({ options });
            },
          };
        }),
      ],
      canActivate: [canGuard('read', 'Document')],
      loadChildren: () =>
        import('@kinet-open-network/client/documents/feature/shell'),
    },
  ],
  createChildrenRoutes: [
    {
      path: '',
      loadComponent: () =>
        import('../feature/tabs/flow-goods-receipt-general-tab.component').then(
          (m) => m.FlowGoodsReceiptGeneralTabComponent,
        ),
    },
  ],
  tableConfig: () => ({
    deletedToggle: true,
  }),
};

export function provideGoodsReceiptFlowConfig() {
  return provideFlowTypeConfig(goodsReceiptFlowConfig);
}
